import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';

import {
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TypedFormBuilder } from '@ui/form/TypedFormBuilder';
import {
  Subject,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EasyPack24LockerPickerComponent } from '@ui/products/products-front/components/delivery-method-picker/easy-pack24-locker-picker/easy-pack24-locker-picker.component';
import { ZalamoDialogService } from 'ZalamoLibrary/src/lib/ui/zalamo-dialog/zalamo-dialog.service';
import { Store } from '@ngrx/store';
import { navigationSetMobileMenuDisabled } from '@ui/navigation/navigation-state/navigation-state.actions';

import {
  GetMineFullDetailsGQL,
  PlaceZalamoFramesOrderGQL,
} from 'projects/photographer-panel/src/graphql';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../../../photographer-client/src/environments/environment';

@Component({
  selector: 'app-frames-quick-order',
  templateUrl: './frames-quick-order.component.html',
  styleUrls: ['./frames-quick-order.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FramesQuickOrderComponent implements OnInit {
  model = this.form.typedGroup<{
    white: number;
    black: number;
    companyName: string;
    companyVatId: string;
    companyStreet: string;
    companyBuildingNumber: string;
    companyFlatNumber: string;
    companyZipCode: string;
    companyCity: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhone: string;
    clientEmail: string;

    deliveryType: 'courier' | 'locker';
    deliveryAddressSameAsCompany: boolean;
    lockerId: string;
    lockerAddress: string[];
  }>({
    white: [0],
    black: [0],
    companyName: ['', Validators.required],
    companyVatId: ['', Validators.required],
    companyStreet: ['', Validators.required],
    companyBuildingNumber: ['', Validators.required],
    companyFlatNumber: [''],
    companyZipCode: ['', Validators.required],
    companyCity: ['', Validators.required],
    clientFirstName: ['', Validators.required],
    clientLastName: ['', Validators.required],
    clientPhone: ['', Validators.required],
    clientEmail: ['', Validators.required],
    deliveryType: ['courier'],
    deliveryAddressSameAsCompany: [true],
    lockerId: [''],
    lockerAddress: [[]],
  });
  courierDeliveryModel = this.form.typedGroup<{
    deliveryStreet: string;
    deliveryBuildingNumber: string;
    deliveryFlatNumber: string;
    deliveryZipCode: string;
    deliveryCity: string;
  }>({
    deliveryStreet: ['', Validators.required],
    deliveryCity: ['', Validators.required],
    deliveryBuildingNumber: ['', Validators.required],
    deliveryZipCode: ['', Validators.required],
    deliveryFlatNumber: [''],
  });

  currentDirection = 'left';
  active = 'left';
  direction = 'right';
  leftElement: TemplateRef<any>;
  rightElement: TemplateRef<any> = null;
  @ViewChild('cart', { static: true }) cart: TemplateRef<any>;
  @ViewChild('buyer', { static: true }) buyer: TemplateRef<any>;
  @ViewChild('finalize', { static: true }) finalize: TemplateRef<any>;
  @ViewChild('tos', { static: true }) tos: TemplateRef<any>;


  orderInProgress$ = new Subject();

  get detailsValid() {
    return (
      this.model.valid &&
      ((this.model.value.deliveryType === 'courier' &&
        (this.model.value.deliveryAddressSameAsCompany ||
          this.courierDeliveryModel.valid)) ||
        (this.model.value.deliveryType === 'locker' &&
          this.model.controls.lockerId.value))
    );
  }

  acceptanceModel = this.form.typedGroup<{
    deliveryDate: boolean;
    tos: boolean;
  }>({
    tos: [false, Validators.requiredTrue],
    deliveryDate: [false, Validators.requiredTrue],
  });
  whiteFocused$ = new Subject();
  blackFocused$ = new Subject();
  total$ = this.model.valueChanges.pipe(
    startWith(this.model.value),

    map(() => {
      const { white, black } = this.model.value;
      return (
        ((isNaN(Number(white)) ? 0 : white) +
          (isNaN(Number(black)) ? 0 : black)) *
        25
      );
    }),
  );
  totalWithDelivery$ = this.total$.pipe(
    map((total) => (total < 1500 ? total + 20 : total)),
  );

  constructor(
    private form: TypedFormBuilder,
    private cd: ChangeDetectorRef,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private zalamoDialogService: ZalamoDialogService,
    private store: Store,
    private getMineFullDetailsGQL: GetMineFullDetailsGQL,
    private organizationService: OrganizationService,
    private placeZalamoFramesOrderGQL: PlaceZalamoFramesOrderGQL,
    private router: Router,
  ) {
    store.dispatch(navigationSetMobileMenuDisabled({ disabled: true }));

    iconRegistry.addSvgIcon(
      'courier',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/courier.svg'),
    );
    iconRegistry.addSvgIcon(
      'locker',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/locker.svg'),
    );
  }

  goToDetailsFromBasket() {
    this.goTo(this.buyer, 'left');
  }

  ngOnInit(): void {
    this.leftElement = this.cart;
    this.organizationService
      .getCurrentOrganization()
      .pipe(
        switchMap(
          (organizationId) =>
            this.getMineFullDetailsGQL.watch({ variables: { organizationId } })
              .valueChanges,
        ),
        throwOnQueryError,
        filter(hasCompleteQueryData),
        take(1),
      )
      .subscribe(({ data: { mineAccountDetails } }) => {
        const firstDigit = (mineAccountDetails.companyAddress ?? '').match(
          /\d/,
        );
        const streetWithoutComasTrimmed = mineAccountDetails.companyAddress
          ?.slice(0, firstDigit.index)
          .replace(',', '')
          .trim();
        const numbers = mineAccountDetails.companyAddress?.slice(
          firstDigit.index,
        );
        const [buildingNumber, flatNumber] = numbers
          ? numbers.split('/')
          : ['', ''];
        this.model.patchValue({
          ...mineAccountDetails,
          companyFlatNumber: flatNumber ?? '',
          companyBuildingNumber: buildingNumber ?? '',
          companyStreet: streetWithoutComasTrimmed,
          clientFirstName: mineAccountDetails.firstName,
          clientLastName: mineAccountDetails.lastName,
          clientPhone: mineAccountDetails.phone,
          clientEmail: mineAccountDetails.email,
        });

        this.courierDeliveryModel.patchValue({
          deliveryBuildingNumber: buildingNumber,
          deliveryCity:
            mineAccountDetails.privateCity ?? mineAccountDetails.companyCity,
          deliveryFlatNumber: flatNumber,
          deliveryZipCode: mineAccountDetails.companyZipCode,
          deliveryStreet: streetWithoutComasTrimmed,
        });
      });
  }

  goToDetails() {
    this.goTo(this.buyer, 'right');
  }

  goToCart() {
    this.goTo(this.cart, 'left');
  }

  goTo(element: TemplateRef<any>, direction) {
    if (direction === 'left') {
      if (this.active === 'left') {
        this.rightElement = element;
        this.active = 'right';
      } else {
        this.leftElement = element;
        this.active = 'left';
      }
    } else {
      if (this.active === 'left') {
        this.rightElement = element;
        this.active = 'right';
      } else {
        this.leftElement = element;
        this.active = 'left';
      }
    }
    this.direction = direction;
    this.cd.detectChanges();
  }

  pickLocker() {
    const ref = this.zalamoDialogService.openFull(
      EasyPack24LockerPickerComponent,
    );
    ref.componentInstance.picked
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(({ name, address }) => {
        this.model.patchValue({
          lockerId: name,
          lockerAddress: [address.line1, address.line2],
        });
        ref.close();
      });
  }

  goToFinalize() {
    this.goTo(this.finalize, 'right');
  }

  showTos() {
    const ref = this.zalamoDialogService.openFull(this.tos);
  }

  placeOrder() {
    this.orderInProgress$.next(true);
    this.placeZalamoFramesOrderGQL
      .mutate({
        variables: {
          frames: {
            ...this.courierDeliveryModel.value,
            ...this.model.value,
          },
        },
      })
      .subscribe(
        ({
          data: {
            placeZalamoFramesOrder: { hash },
          },
        }) => {
          location.href = `${environment.orderDetailsUrl}/order-details/${hash}`;
        },
      );
  }
}
